import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard, ArrowUpRight, ArrowDownRight, TrendingUp, Users, Check, Mail,
  ExternalLink, Zap, Shield, Crown, Star, X, Download, AlertTriangle, Lock,
} from 'lucide-react';
import { StatCard, PlanBadge, Modal, Spinner } from '@/components/shared/UIComponents';
import { ContextualHelp } from '@/components/help/ContextualHelp';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { createB2BCheckout, updateSeats, fetchInvoices, updateSubscription, fetchPaymentMethod, createBillingPortalUrl, fetchActivePlans, fetchPlanFeatureDefaults } from '@/lib/api';
import type { PaymentMethodInfo } from '@/lib/api';
import { usePlatformStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import type { Tenant, TenantPlan, PlanName, StripeInvoice } from '@/types';

// ── Plan icons ──────────────────────────────────────────────────────────────
const PLAN_ICONS: Record<PlanName, typeof Star> = {
  free: Star, pro: Zap, business: Shield, enterprise: Crown,
};

// ── Feature labels for comparison ───────────────────────────────────────────
const FEATURE_LABELS: Record<string, string> = {
  ai_trip_builder: 'AI Trip Builder', budget_manager: 'Budget Manager',
  expense_splitting: 'Expense Splitting', live_dashboard: 'Live Dashboard',
  voice_interpreter: 'Voice Interpreter', cultural_hub: 'Cultural Hub',
  packing_list: 'Packing List', receipt_ocr: 'Receipt OCR',
  collaboration: 'Collaboration', local_agent: 'Local Agent',
  zoom_meetings: 'Zoom Meetings', offline_mode: 'Offline Mode',
  api_access: 'API Access', sso_saml: 'SSO / SAML',
  custom_branding: 'Custom Branding', custom_domain: 'Custom Domain',
};

const STATUS_COLORS: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-600 dark:text-emerald-400',
  open: 'bg-amber-100 text-amber-600 dark:text-amber-400',
  draft: 'bg-surface-100 text-surface-600',
  void: 'bg-surface-100 text-surface-500',
  uncollectible: 'bg-red-100 text-red-600 dark:text-red-400',
};

export function BillingPage({ tenant }: { tenant: Tenant }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectTenant = usePlatformStore(s => s.selectTenant);
  const { user } = useAuth();

  // ── Upgrade modal state ───────────────────────────────────────────────
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanName | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [seats, setSeats] = useState(Math.max(tenant.current_user_count || 1, 1));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // ── Seats modal state ─────────────────────────────────────────────────
  const [showSeatsModal, setShowSeatsModal] = useState(false);
  const [newSeatCount, setNewSeatCount] = useState(tenant.max_users || 1);
  const [seatsLoading, setSeatsLoading] = useState(false);
  const [seatsError, setSeatsError] = useState<string | null>(null);

  // ── Invoice state ─────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // ── Payment method state ─────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodInfo | null>(null);
  const [pmLoading, setPmLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // ── Downgrade modal state ─────────────────────────────────────────────
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState<PlanName | null>(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [downgradeError, setDowngradeError] = useState<string | null>(null);
  const [pendingCancellation, setPendingCancellation] = useState(false);

  // ── All plans from DB ───────────────────────────────────────────────
  const [allPlans, setAllPlans] = useState<TenantPlan[]>([]);
  const [currentPlanFeatures, setCurrentPlanFeatures] = useState<Record<string, boolean>>({});
  const [targetPlanFeatures, setTargetPlanFeatures] = useState<Record<string, boolean>>({});

  const plan = tenant.plan as TenantPlan | undefined;
  const planName = (plan?.name || 'free') as PlanName;
  const monthlyCost = (plan?.price_per_seat_monthly || 0) * (tenant.current_user_count || 1);
  const currentRank = plan?.rank ?? 0;

  // Helper to find a plan from allPlans by name
  const getPlanByName = (name: PlanName) => allPlans.find(p => p.name === name);

  const upgradePlans = allPlans
    .filter(p => p.rank > currentRank)
    .sort((a, b) => a.rank - b.rank)
    .map(p => p.name as PlanName);
  const downgradePlans = allPlans
    .filter(p => p.rank < currentRank)
    .sort((a, b) => a.rank - b.rank)
    .map(p => p.name as PlanName);

  // ── One-shot Starter rule ──────────────────────────────────────────
  // The DB trigger blocks any update of tenants.plan_id back to a free
  // plan (price=0) once tenants.starter_plan_used_at has been stamped.
  // Reflect that in the UI: detect free plans by PRICE rather than by
  // name (robust if "Starter" gets renamed) and lock those options in
  // the downgrade modal with a "contact super admin" explanation
  // instead of letting the click bounce off the trigger.
  const hasUsedStarter = tenant.starter_plan_used_at != null;
  const isFreePlanName = (name: PlanName): boolean => {
    const p = getPlanByName(name);
    return p ? (p.price_per_seat_monthly === 0 && p.price_per_seat_annual === 0) : false;
  };
  const isPlanLocked = (name: PlanName): boolean =>
    hasUsedStarter && isFreePlanName(name);

  // ── Load all plans from DB ──────────────────────────────────────────────
  useEffect(() => {
    fetchActivePlans().then(setAllPlans).catch(err => console.error('Failed to fetch plans:', err));
  }, []);

  // ── Load current plan features ─────────────────────────────────────────
  useEffect(() => {
    if (plan?.id) {
      fetchPlanFeatureDefaults(plan.id).then(rows => {
        const map: Record<string, boolean> = {};
        rows.forEach(r => map[r.feature_key] = r.is_enabled);
        setCurrentPlanFeatures(map);
      });
    }
  }, [plan?.id]);

  // ── Handle success redirect from Stripe ─────────────────────────────────
  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      setShowSuccess(true);
      selectTenant(tenant.id); // Refresh tenant data from DB
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('upgraded');
      newParams.delete('plan');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, selectTenant, tenant.id]);

  // ── Load invoices + payment method on mount ────────────────────────────
  useEffect(() => {
    if (!tenant.stripe_customer_id) return;
    setInvoicesLoading(true);
    fetchInvoices(tenant.id)
      .then(setInvoices)
      .catch(err => console.error('Failed to fetch invoices:', err))
      .finally(() => setInvoicesLoading(false));

    setPmLoading(true);
    fetchPaymentMethod(tenant.id)
      .then(setPaymentMethod)
      .catch(err => console.error('Failed to fetch payment method:', err))
      .finally(() => setPmLoading(false));
  }, [tenant.id, tenant.stripe_customer_id]);

  // ── Upgrade handlers ──────────────────────────────────────────────────
  const openUpgradeModal = () => {
    setSelectedPlan(upgradePlans[0] || null);
    setSeats(Math.max(tenant.current_user_count || 1, 1));
    setBillingCycle('monthly');
    setError(null);
    setShowUpgradeModal(true);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    if (selectedPlan === 'enterprise') {
      window.open('mailto:sales@discoverygem.com?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { url } = await createB2BCheckout({
        tenantId: tenant.id, planName: selectedPlan, billingCycle, seats, adminEmail: user?.email || '',
      });
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const getSelectedPrice = () => {
    if (!selectedPlan) return 0;
    const sp = getPlanByName(selectedPlan);
    if (!sp) return 0;
    const price = billingCycle === 'annual' ? sp.price_per_seat_annual : sp.price_per_seat_monthly;
    return price * seats;
  };

  // ── Seats handlers ────────────────────────────────────────────────────
  const openSeatsModal = () => {
    setNewSeatCount(tenant.max_users || 1);
    setSeatsError(null);
    setShowSeatsModal(true);
  };

  const handleUpdateSeats = async () => {
    setSeatsLoading(true);
    setSeatsError(null);
    try {
      await updateSeats({ tenantId: tenant.id, newQuantity: newSeatCount });
      await selectTenant(tenant.id);
      setShowSeatsModal(false);
    } catch (err: any) {
      setSeatsError(err.message || 'Failed to update seats');
    } finally {
      setSeatsLoading(false);
    }
  };

  // ── Downgrade handlers ────────────────────────────────────────────────
  const openDowngradeModal = () => {
    // Auto-select the lowest tier the tenant is allowed to land on. If
    // the absolute lowest is the locked Starter plan (already used),
    // walk up the list until we find an unlocked one. Falls back to
    // null when every downgrade option is locked.
    const firstUnlocked = [...downgradePlans].reverse().find(p => !isPlanLocked(p)) ?? null;
    setSelectedDowngradePlan(firstUnlocked);
    setDowngradeError(null);
    setShowDowngradeModal(true);
  };

  // Fetch target plan features when downgrade plan changes
  useEffect(() => {
    if (selectedDowngradePlan) {
      const targetPlan = getPlanByName(selectedDowngradePlan);
      if (targetPlan) {
        fetchPlanFeatureDefaults(targetPlan.id).then(rows => {
          const map: Record<string, boolean> = {};
          rows.forEach(r => map[r.feature_key] = r.is_enabled);
          setTargetPlanFeatures(map);
        });
      }
    }
  }, [selectedDowngradePlan, allPlans]);

  const featureLosses = selectedDowngradePlan
    ? Object.entries(currentPlanFeatures).filter(
        ([key, val]) => val && !targetPlanFeatures[key]
      )
    : [];

  const handleDowngrade = async () => {
    if (!selectedDowngradePlan) return;
    setDowngradeLoading(true);
    setDowngradeError(null);
    try {
      const action = selectedDowngradePlan === 'free' ? 'cancel' : 'switch';
      const result = await updateSubscription({ tenantId: tenant.id, targetPlanName: selectedDowngradePlan, action });
      await selectTenant(tenant.id);
      setShowDowngradeModal(false);
      if (result.action === 'cancel') {
        setPendingCancellation(true);
      } else {
        setShowSuccess(true);
      }
    } catch (err: any) {
      setDowngradeError(err.message || 'Failed to downgrade plan');
    } finally {
      setDowngradeLoading(false);
    }
  };

  // ── Billing portal handler ───────────────────────────────────────────
  const handleManageCard = async () => {
    setPortalLoading(true);
    try {
      const url = await createBillingPortalUrl(tenant.id);
      window.open(url, '_blank');
    } catch (err: any) {
      console.error('Failed to open billing portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Format unix timestamp ─────────────────────────────────────────────
  const formatUnixDate = (ts: number) => formatDate(new Date(ts * 1000));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-surface-900">Billing</h1>
        <p className="text-sm text-surface-500 mt-1">Manage your subscription, seats, and invoices</p>
      </div>

      {/* Success banner */}
      {showSuccess && (
        <div className="p-4 rounded-xl banner-emerald border animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <Check size={16} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-emerald-800">Plan updated successfully!</div>
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                Your plan is now active. Features and limits have been updated.
              </div>
            </div>
            <button onClick={() => setShowSuccess(false)} className="p-1 rounded hover:bg-emerald-100">
              <X size={14} className="text-emerald-500" />
            </button>
          </div>
        </div>
      )}

      {/* Pending cancellation banner */}
      {pendingCancellation && (
        <div className="p-4 rounded-xl banner-amber border animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-800">Subscription scheduled to cancel</div>
              <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                Your {plan?.display_name || planName} plan will remain active until the end of your current billing period. After that, you'll be moved to the Free plan.
              </div>
            </div>
            <button onClick={() => setPendingCancellation(false)} className="p-1 rounded hover:bg-amber-100">
              <X size={14} className="text-amber-500" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Monthly Cost" value={formatCurrency(monthlyCost)} change="per month" changeType="neutral" icon={<CreditCard size={18} />} delay={0} />
        <StatCard label="Seats Used" value={`${tenant.current_user_count || 0} / ${tenant.max_users || '∞'}`} icon={<Users size={18} />} delay={50} />
        <StatCard label="Per Seat" value={plan?.price_per_seat_monthly ? formatCurrency(plan.price_per_seat_monthly) : 'Free'} change="/seat/month" changeType="neutral" icon={<TrendingUp size={18} />} delay={100} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current plan */}
        <div className="lg:col-span-2 glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-surface-900">Current Plan</h3>
            <PlanBadge plan={planName} label={plan?.display_name} />
          </div>

          <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl mb-4">
            <div className="text-lg font-bold text-surface-900">{plan?.display_name || planName}</div>
            <div className="text-sm text-surface-500 mt-1">{plan?.description || ''}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
              <div>
                <div className="text-xs text-surface-500">Price</div>
                <div className="text-sm font-medium text-surface-900">
                  {plan?.price_per_seat_monthly ? `${formatCurrency(plan.price_per_seat_monthly)}/seat/mo` : 'Free'}
                </div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Max Users</div>
                <div className="text-sm font-medium text-surface-900">{plan?.max_users || '∞'}</div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Trips/Month</div>
                <div className="text-sm font-medium text-surface-900">{plan?.max_trips_per_month || '∞'}</div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Storage</div>
                <div className="text-sm font-medium text-surface-900">
                  {plan?.max_storage_mb ? (plan.max_storage_mb >= 1024 ? `${plan.max_storage_mb / 1024} GB` : `${plan.max_storage_mb} MB`) : '∞'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {planName !== 'enterprise' && upgradePlans.length > 0 ? (
              <button onClick={openUpgradeModal} className="btn-primary text-sm">
                <ArrowUpRight size={14} /> Upgrade Plan
              </button>
            ) : planName === 'enterprise' ? (
              <a href="mailto:sales@discoverygem.com" className="btn-primary text-sm inline-flex items-center gap-1">
                <Mail size={14} /> Contact Sales
              </a>
            ) : null}
            <button
              className="btn-secondary text-sm"
              disabled={!tenant.stripe_subscription_id}
              onClick={openSeatsModal}
              title={!tenant.stripe_subscription_id ? 'Upgrade to a paid plan to manage seats' : undefined}
            >
              <Users size={14} /> Manage Seats
            </button>
            {planName !== 'free' && (
              <button
                onClick={openDowngradeModal}
                className="btn-secondary text-sm text-red-600 dark:text-red-400 hover:text-red-600 dark:text-red-400 border-red-800/40 hover:border-red-800/40"
              >
                <ArrowDownRight size={14} /> Downgrade
              </button>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="space-y-4">
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-3">Payment Method</h3>
            {pmLoading ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : paymentMethod?.has_payment_method ? (
              <>
                <div className="flex items-center gap-3 p-3 bg-surface-50 border border-surface-200 rounded-lg">
                  <div className="p-1.5 rounded bg-surface-200">
                    <CreditCard size={14} className="text-surface-600" />
                  </div>
                  <div>
                    <div className="text-sm text-surface-900 capitalize">
                      {paymentMethod.brand} •••• {paymentMethod.last4}
                    </div>
                    <div className="text-xs text-surface-500">
                      Expires {String(paymentMethod.exp_month).padStart(2, '0')}/{String(paymentMethod.exp_year).slice(-2)}
                    </div>
                  </div>
                </div>
                <button onClick={handleManageCard} disabled={portalLoading} className="btn-secondary text-sm mt-3 w-full justify-center">
                  {portalLoading ? <Spinner size="sm" /> : <CreditCard size={14} />}
                  Manage Card
                </button>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-surface-500 mb-3">No payment method on file</p>
                {tenant.stripe_customer_id ? (
                  <button onClick={handleManageCard} disabled={portalLoading} className="btn-secondary text-sm">
                    {portalLoading ? <Spinner size="sm" /> : <CreditCard size={14} />}
                    Add Card
                  </button>
                ) : (
                  <p className="text-xs text-surface-400">Upgrade to a paid plan to add a payment method</p>
                )}
              </div>
            )}
          </div>

          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-2">Plan Comparison</h3>
            <div className="space-y-2">
              {allPlans.map(p => {
                const Icon = PLAN_ICONS[p.name as PlanName] || Star;
                const priceLabel = p.price_per_seat_monthly === 0
                  ? (p.name === 'enterprise' ? 'Custom' : 'Free')
                  : `${formatCurrency(p.price_per_seat_monthly)}/seat/mo`;
                return (
                  <div key={p.id} className={`flex items-center justify-between p-2 rounded-lg transition-colors ${p.name === planName ? 'bg-brand-500/10 border border-brand-500/20' : 'hover:bg-surface-50'}`}>
                    <div className="flex items-center gap-2">
                      {p.name === planName ? <Check size={12} className="text-brand-600" /> : <Icon size={12} className="text-surface-400" />}
                      <span className="text-sm text-surface-700 capitalize">{p.name}</span>
                    </div>
                    <span className="text-xs text-surface-500">{priceLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Invoice History ─────────────────────────────────────────────── */}
      <div className="glass-panel overflow-hidden">
        <div className="p-5 border-b border-surface-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-surface-900">Invoice History</h3>
          {tenant.stripe_customer_id && (
            <a href={`https://dashboard.stripe.com/customers/${tenant.stripe_customer_id}`}
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1">
              View in Stripe <ExternalLink size={10} />
            </a>
          )}
        </div>

        {invoicesLoading ? (
          <div className="px-5 py-12 flex justify-center">
            <Spinner />
          </div>
        ) : invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50/50">
                  <th className="text-left px-5 py-3 text-xs font-medium text-surface-500">Invoice</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-surface-500">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-surface-500">Amount</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-surface-500">Status</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-surface-500">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} className="border-b border-surface-100 hover:bg-surface-50/50">
                    <td className="px-5 py-3">
                      {inv.hosted_invoice_url ? (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                          className="text-brand-600 hover:text-brand-700 font-medium">
                          {inv.number || inv.id.slice(-8)}
                        </a>
                      ) : (
                        <span className="text-surface-700">{inv.number || inv.id.slice(-8)}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-surface-600">{formatUnixDate(inv.created)}</td>
                    <td className="px-5 py-3 font-medium text-surface-900">{formatCurrency(inv.amount_paid || inv.amount_due)}</td>
                    <td className="px-5 py-3">
                      {inv.status === 'open' || inv.status === 'uncollectible' ? (
                        <ContextualHelp helpKey={inv.status === 'open' ? 'invoice-open' : 'invoice-uncollectible'}>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[inv.status] || 'bg-surface-100 text-surface-600'}`}>
                            {inv.status}
                          </span>
                        </ContextualHelp>
                      ) : (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[inv.status] || 'bg-surface-100 text-surface-600'}`}>
                          {inv.status}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                          className="inline-flex p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-600">
                          <Download size={14} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-12 text-center">
            <CreditCard size={24} className="mx-auto text-surface-300 mb-3" />
            <p className="text-sm text-surface-500">No invoices yet</p>
            <p className="text-xs text-surface-400 mt-1">
              {planName === 'free'
                ? 'Upgrade to a paid plan to start receiving invoices.'
                : 'Invoices will appear here after your first billing cycle.'}
            </p>
          </div>
        )}
      </div>

      {/* ── Upgrade Modal ──────────────────────────────────────────────── */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade Your Plan"
        subtitle="Choose a plan to unlock more features for your organisation"
        wide
      >
        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center gap-1 mb-6 p-1 bg-surface-100 rounded-xl w-fit mx-auto">
          <button onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-surface-0 text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>
            Monthly
          </button>
          <button onClick={() => setBillingCycle('annual')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-surface-0 text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>
            Annual <ContextualHelp helpKey="annual-savings"><span className="text-emerald-600 dark:text-emerald-400 text-xs ml-1">Save 20%</span></ContextualHelp>
          </button>
        </div>

        {/* Plan cards */}
        <UpgradePlanCards
          upgradePlans={upgradePlans}
          allPlans={allPlans}
          planName={planName}
          currentPlanFeatures={currentPlanFeatures}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          billingCycle={billingCycle}
        />

        {/* Seat count + price preview */}
        {selectedPlan && selectedPlan !== 'enterprise' && (() => {
          const sp = getPlanByName(selectedPlan);
          if (!sp) return null;
          const perSeatPrice = billingCycle === 'annual' ? sp.price_per_seat_annual : sp.price_per_seat_monthly;
          return (
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-surface-700 font-medium">Seats:</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSeats(s => Math.max(1, s - 1))} className="w-7 h-7 rounded-lg bg-surface-0 border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-200">−</button>
                    <input type="number" min={1} max={sp.max_users || 9999} value={seats}
                      onChange={e => setSeats(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center text-sm font-medium border border-surface-200 rounded-lg py-1" />
                    <button onClick={() => setSeats(s => Math.min(sp.max_users || 9999, s + 1))} className="w-7 h-7 rounded-lg bg-surface-0 border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-200">+</button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-surface-500">{seats} seat{seats !== 1 ? 's' : ''} × {formatCurrency(perSeatPrice)}/mo</div>
                  <div className="text-lg font-bold text-surface-900">{formatCurrency(getSelectedPrice())}<span className="text-xs font-normal text-surface-500">/mo</span></div>
                  {billingCycle === 'annual' && (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400">You save {formatCurrency((sp.price_per_seat_monthly - sp.price_per_seat_annual) * seats)}/mo vs monthly</div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {error && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400 mb-4">{error}</div>}

        <div className="flex justify-end gap-3">
          <button onClick={() => setShowUpgradeModal(false)} className="btn-secondary text-sm" disabled={loading}>Cancel</button>
          <button onClick={handleCheckout} disabled={!selectedPlan || loading} className="btn-primary text-sm min-w-[180px] justify-center">
            {loading ? <Spinner size="sm" /> : selectedPlan === 'enterprise' ? <><Mail size={14} /> Contact Sales</> : <><CreditCard size={14} /> Subscribe via Stripe</>}
          </button>
        </div>
      </Modal>

      {/* ── Manage Seats Modal ─────────────────────────────────────────── */}
      <Modal
        open={showSeatsModal}
        onClose={() => setShowSeatsModal(false)}
        title="Manage Seats"
        subtitle="Adjust the number of seats on your subscription"
      >
        <div className="space-y-4">
          <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-surface-500">Current seats</div>
                <div className="text-2xl font-bold text-surface-900">{tenant.max_users || '∞'}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-surface-500">In use</div>
                <div className="text-2xl font-bold text-surface-900">{tenant.current_user_count || 0}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-surface-200">
              <label className="text-sm text-surface-700 font-medium flex-1">New seat count:</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setNewSeatCount(s => Math.max(tenant.current_user_count || 1, s - 1))}
                  className="w-8 h-8 rounded-lg bg-surface-0 border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-200">−</button>
                <input type="number" min={tenant.current_user_count || 1}
                  max={plan?.max_users || 9999}
                  value={newSeatCount}
                  onChange={e => setNewSeatCount(Math.max(tenant.current_user_count || 1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center text-sm font-medium border border-surface-200 rounded-lg py-1.5" />
                <button onClick={() => setNewSeatCount(s => Math.min(plan?.max_users || 9999, s + 1))}
                  className="w-8 h-8 rounded-lg bg-surface-0 border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-200">+</button>
              </div>
            </div>
          </div>

          {newSeatCount !== (tenant.max_users || 0) && (
            <div className="p-3 rounded-lg banner-blue border text-xs text-blue-400">
              {newSeatCount > (tenant.max_users || 0)
                ? <ContextualHelp helpKey="prorated-charge">
                    <span>Adding {newSeatCount - (tenant.max_users || 0)} seat(s). The change will be prorated on your next invoice.</span>
                  </ContextualHelp>
                : <ContextualHelp helpKey="prorated-credit">
                    <span>Removing {(tenant.max_users || 0) - newSeatCount} seat(s). You'll receive a prorated credit.</span>
                  </ContextualHelp>}
            </div>
          )}

          {seatsError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{seatsError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowSeatsModal(false)} className="btn-secondary text-sm" disabled={seatsLoading}>Cancel</button>
            <button onClick={handleUpdateSeats}
              disabled={seatsLoading || newSeatCount === (tenant.max_users || 0)}
              className="btn-primary text-sm min-w-[140px] justify-center">
              {seatsLoading ? <Spinner size="sm" /> : <><Users size={14} /> Update Seats</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Downgrade Modal ────────────────────────────────────────────── */}
      <Modal
        open={showDowngradeModal}
        onClose={() => setShowDowngradeModal(false)}
        title="Downgrade Plan"
        subtitle="Select a lower plan — you'll keep access until the end of your billing period"
      >
        <div className="space-y-4">
          {/* Plan selection */}
          <div className="space-y-2">
            {downgradePlans.map(p => {
              const dp = getPlanByName(p);
              if (!dp) return null;
              const Icon = PLAN_ICONS[p] || Star;
              const isSelected = selectedDowngradePlan === p;
              const locked     = isPlanLocked(p);
              return (
                <button
                  key={p}
                  onClick={() => { if (!locked) setSelectedDowngradePlan(p); }}
                  disabled={locked}
                  title={locked ? 'Already used — contact super admin' : undefined}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    locked
                      ? 'border-surface-200 bg-surface-100/40 cursor-not-allowed opacity-60'
                      : isSelected
                        ? 'border-red-800/40 bg-red-900/10'
                        : 'border-surface-200 hover:border-surface-300 bg-surface-0'
                  }`}
                >
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: dp.color + '15' }}>
                    <Icon size={14} style={{ color: dp.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                      {dp.display_name}
                      {locked && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-200 text-surface-600">
                          <Lock size={9} /> Locked
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-surface-500">
                      {locked
                        ? 'Already used — contact super admin to re-enable'
                        : isFreePlanName(p)
                          ? 'Free forever'
                          : `${formatCurrency(dp.price_per_seat_monthly)}/seat/mo`}
                    </div>
                  </div>
                  {isSelected && !locked && <Check size={16} className="text-red-500" />}
                </button>
              );
            })}
          </div>

          {/* Explainer when the Starter plan is locked — visible at all times
              while the modal is open, so tenants understand why the lowest
              tier card is greyed out without having to hover. */}
          {hasUsedStarter && (
            <div className="p-3 rounded-lg banner-amber border text-xs text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Starter plan is one-time use</div>
                  <div className="mt-0.5 text-amber-700 dark:text-amber-400">
                    Your tenant has already used the Starter plan. To return to it,
                    please <a href="mailto:support@discoverygem.com?subject=Re-enable%20Starter%20plan" className="underline font-medium">contact the super admin</a>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Feature loss warnings */}
          {featureLosses.length > 0 && (
            <div className="p-4 rounded-xl banner-amber border">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-amber-800">Features you'll lose</span>
              </div>
              <div className="space-y-1">
                {featureLosses.map(([key]) => (
                  <div key={key} className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <X size={10} className="text-red-500" />
                    <span>{FEATURE_LABELS[key] || key}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action description */}
          <div className="p-3 rounded-lg bg-surface-50 border border-surface-200 text-xs text-surface-600">
            {selectedDowngradePlan === 'free'
              ? <ContextualHelp helpKey="plan-cancellation">
                  <span>Your subscription will be cancelled at the end of the current billing period. You'll keep full access until then.</span>
                </ContextualHelp>
              : <ContextualHelp helpKey="prorated-credit">
                  <span>Your plan will be changed immediately with a prorated credit applied to your account.</span>
                </ContextualHelp>}
          </div>

          {downgradeError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{downgradeError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowDowngradeModal(false)} className="btn-secondary text-sm" disabled={downgradeLoading}>Cancel</button>
            <button
              onClick={handleDowngrade}
              disabled={
                !selectedDowngradePlan
                || downgradeLoading
                || (selectedDowngradePlan != null && isPlanLocked(selectedDowngradePlan))
              }
              className="text-sm px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2 min-w-[160px] justify-center"
            >
              {downgradeLoading ? <Spinner size="sm" /> : <><ArrowDownRight size={14} /> Confirm Downgrade</>}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* Upgrade Plan Cards (extracted to use async feature fetching)               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function UpgradePlanCards({ upgradePlans, allPlans, planName, currentPlanFeatures, selectedPlan, setSelectedPlan, billingCycle }: {
  upgradePlans: PlanName[];
  allPlans: TenantPlan[];
  planName: PlanName;
  currentPlanFeatures: Record<string, boolean>;
  selectedPlan: PlanName | null;
  setSelectedPlan: (p: PlanName) => void;
  billingCycle: 'monthly' | 'annual';
}) {
  const [upgradeFeaturesMap, setUpgradeFeaturesMap] = useState<Record<string, Record<string, boolean>>>({});

  useEffect(() => {
    upgradePlans.forEach(p => {
      const planObj = allPlans.find(ap => ap.name === p);
      if (planObj) {
        fetchPlanFeatureDefaults(planObj.id).then(rows => {
          const map: Record<string, boolean> = {};
          rows.forEach(r => map[r.feature_key] = r.is_enabled);
          setUpgradeFeaturesMap(prev => ({ ...prev, [p]: map }));
        });
      }
    });
  }, [upgradePlans, allPlans]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
      {upgradePlans.map(p => {
        const dp = allPlans.find(ap => ap.name === p);
        if (!dp) return null;
        const Icon = PLAN_ICONS[p] || Star;
        const price = billingCycle === 'annual' ? dp.price_per_seat_annual : dp.price_per_seat_monthly;
        const isSelected = selectedPlan === p;
        const planFeats = upgradeFeaturesMap[p] || {};
        return (
          <button key={p} onClick={() => setSelectedPlan(p)}
            className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-brand-500 bg-brand-500/5 ring-1 ring-brand-200' : 'border-surface-200 hover:border-surface-300 bg-surface-0'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg" style={{ backgroundColor: dp.color + '15' }}>
                <Icon size={14} style={{ color: dp.color }} />
              </div>
              <span className="text-sm font-semibold text-surface-900">{dp.display_name}</span>
            </div>
            <div className="text-xs text-surface-500 mb-3">{dp.description}</div>
            <div className="text-lg font-bold text-surface-900">
              {p === 'enterprise' ? 'Custom' : <>{formatCurrency(price)}<span className="text-xs font-normal text-surface-500">/seat/mo</span></>}
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="text-xs text-surface-500"><span className="font-medium text-surface-700">{dp.max_users || '∞'}</span> max users</div>
              <div className="text-xs text-surface-500"><span className="font-medium text-surface-700">{dp.max_storage_mb >= 1024 ? `${dp.max_storage_mb / 1024} GB` : `${dp.max_storage_mb} MB`}</span> storage</div>
            </div>
            <div className="mt-3 pt-3 border-t border-surface-100">
              <div className="text-[10px] uppercase tracking-wider text-surface-400 mb-1.5">New features</div>
              {Object.entries(planFeats).map(([key, val]) => {
                if (val && !currentPlanFeatures[key]) {
                  return <div key={key} className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400"><Check size={10} /><span>{FEATURE_LABELS[key] || key}</span></div>;
                }
                return null;
              })}
            </div>
          </button>
        );
      })}
    </div>
  );
}
